import { KeyboardEvent, useCallback } from 'react';

import { AddressSuggestion } from '../types/AddressSuggestion';
import { AddressSuggestionSelection } from '../types/AddressSuggestionSelection';
import useAddressSuggestionStore from '../useAddressSuggestionStore';

export const useAddressSuggestionSelection = (
  onSelect: (suggestion: AddressSuggestion) => void,
): AddressSuggestionSelection => {
  const {
    addressSuggestions,
    computed: { hasAddressSuggestions },
    selectedIndex,
    setSelectedIndex,
    clearSuggestions,
  } = useAddressSuggestionStore();

  const onSuggestionSelect = useCallback(
    (index: number): void => {
      if (index < 0 || index >= addressSuggestions.length) {
        return;
      }

      const suggestion = addressSuggestions[index];

      onSelect(suggestion);
      clearSuggestions();
    },
    [addressSuggestions, onSelect, clearSuggestions],
  );

  const onSuggestionKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (!hasAddressSuggestions) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();

          setSelectedIndex(
            Math.min(selectedIndex + 1, addressSuggestions.length - 1),
          );

          break;
        case 'ArrowUp':
          event.preventDefault();

          setSelectedIndex(Math.max(selectedIndex - 1, -1));

          break;
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0) {
            onSuggestionSelect(selectedIndex);
          }

          break;
        case 'Escape':
          clearSuggestions();

          break;
        default:
          break;
      }
    },
    [
      addressSuggestions,
      hasAddressSuggestions,
      selectedIndex,
      clearSuggestions,
      onSuggestionSelect,
      setSelectedIndex,
    ],
  );

  return { onSuggestionKeyDown, onSuggestionSelect };
};
